import type { ReactNode } from "react";
import { CloudOff } from "lucide-react";
import { copy } from "../lib/copy";
import { useOnline } from "../lib/useOnline";

/**
 * The page frame: title, offline notice, scrolling body.
 *
 * This exists so the offline state is not something each screen remembers to
 * implement — it is a required state on every screen, so it belongs to the
 * frame. `action` floats over the body; the body reserves room for it.
 */
type Props = {
  title: string;
  children: ReactNode;
  /**
   * The screen's one primary action — a `PrimaryAction`. It floats, so the
   * frame only has to keep the last row of content clear of it.
   */
  action?: ReactNode;
  /** Sits above the title, for screens reached from another screen. */
  back?: ReactNode;
  /**
   * One control beside the title, for acting on the thing the title *is* —
   * renaming a task, paging the month. Sits on the title's row rather than in
   * the content, because it belongs to the heading and would otherwise read as
   * the first field.
   */
  titleAction?: ReactNode;
  /**
   * Whether a tab bar sits below this screen. False on the screens shown
   * before there is anything to navigate between — signing in and joining.
   */
  tabBar?: boolean;
};

export function Screen({ title, children, action, back, titleAction, tabBar = true }: Props) {
  const online = useOnline();

  /*
   * Bottom padding: the tab bar's strip, plus — when there is a floating
   * action — the pill's 56px and 16px either side of it, so the last row can
   * scroll fully clear of the pill rather than end underneath it.
   */
  const bottom = !tabBar
    ? "pb-[calc(env(safe-area-inset-bottom)+1.5rem)]"
    : action
      ? "pb-[calc(env(safe-area-inset-bottom)+var(--spacing-tab-bar)+5.5rem)]"
      : "pb-[calc(env(safe-area-inset-bottom)+var(--spacing-tab-bar)+1rem)]";

  return (
    <div className="mx-auto flex h-full max-w-md flex-col">
      {/* 20px to the title's left edge, 8px on the right so a title action's
          44px box sits on the gutter rather than inside it. */}
      <header className="min-h-18 shrink-0 pt-5 pr-2 pb-2 pl-5">
        {back}
        {/* The action keeps its own 44px box and the title takes the rest.
            `items-start` so a title wrapping to three lines leaves the control
            beside the first line rather than floating to the middle. */}
        <div className="flex items-start gap-2">
          <h1 className="min-w-0 flex-1 pt-2 text-title font-semibold text-balance wrap-anywhere hyphens-auto">
            {title}
          </h1>
          {titleAction}
        </div>
      </header>

      {!online && (
        <p className="mx-4 mb-4 flex shrink-0 items-start gap-3 rounded-card border border-line bg-surface px-4 py-3 text-meta text-muted">
          <CloudOff aria-hidden strokeWidth={1.75} className="mt-px size-4.5 shrink-0" />
          {copy.states.offline}
        </p>
      )}

      <div className={`min-h-0 flex-1 overflow-y-auto px-4 ${bottom}`}>{children}</div>

      {action}
    </div>
  );
}
